import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";

import CandidateStatusService from "@/services/candidateStatusService";
import { mapTransitionIdsToNames } from "../utils";
import { getCandidateStatusTypeMeta } from "../constants";
import { getStatusType } from "../statusFlags";

const toText = (value) => {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return "—";
};

const Row = ({ label, value, renderValue }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-2 border-b border-border-primary last:border-b-0">
    <div className="text-xs text-text-secondary">{toText(label)}</div>
    <div className="text-sm text-text-primary font-medium break-words">
      {renderValue ?? toText(value)}
    </div>
  </div>
);

const statusTypeKey = (status) => getStatusType(status);

// One source of truth for the pill's classes — the same meta the table's
// CategoryPill renders. Hand-rolled class strings here used to drift (no
// border, different dark variants), so the badge changed shape between the
// list and this modal and between light and dark themes.
const StatusTypeBadgeInline = ({ status }) => {
  const meta = getCandidateStatusTypeMeta(statusTypeKey(status));

  return (
    <span
      className={`inline-flex rounded-full px-2 py-1 text-[11px] font-medium ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const ViewCandidateStatusModal = ({ open, onModelClose, status, flowId }) => {
  const [currentStatus, setCurrentStatus] = useState(null);
  const [allStatuses, setAllStatuses] = useState([]);

  useEffect(() => {
    if (!open) return;
    setCurrentStatus(status ?? null);
  }, [open, status]);

  useEffect(() => {
    const fetchStatuses = async () => {
      if (!open || !flowId) return;

      try {
        const res = await CandidateStatusService.list({
          flowId,
          page: 1,
          per_page: 100,
          sort_by: "name",
          sort_order: "asc",
        });

        setAllStatuses(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setAllStatuses([]);
      }
    };

    fetchStatuses();
  }, [open, flowId]);

  const isOpen = open && !!currentStatus;

  const transitionNames = useMemo(
    () =>
      mapTransitionIdsToNames(
        currentStatus?.allowedTransitionIds,
        allStatuses
      ),
    [currentStatus?.allowedTransitionIds, allStatuses]
  );

  const transitionsLabel =
    currentStatus?.isRejected || currentStatus?.isHired
      ? "Terminal (no outgoing transitions)"
      : transitionNames.length
      ? transitionNames.join(", ")
      : "No outgoing transitions";

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onModelClose?.()}>
      <DialogContent className="w-[640px] md:w-full flex flex-col p-0">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">
              View Candidate Status
            </DialogTitle>

            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
              {toText(currentStatus?.name) || "Candidate status details"}
            </DialogDescription>
          </div>

          <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto custom-scrollbar">
          <div className="rounded-2xl border border-border-primary bg-background-form p-4">
            <Row label="Name" value={currentStatus?.name} />
            <Row
              label="Type"
              renderValue={<StatusTypeBadgeInline status={currentStatus} />}
            />
            <Row label="Description" value={currentStatus?.description} />
            <Row
              label="Initial"
              value={currentStatus?.isInitial ? "Yes" : "No"}
            />
            <Row label="Hired" value={currentStatus?.isHired ? "Yes" : "No"} />
            <Row
              label="Rejected"
              value={currentStatus?.isRejected ? "Yes" : "No"}
            />
            <Row
              label="Reusable"
              value={currentStatus?.isReusable ? "Yes" : "No"}
            />
            <Row
              label="Interview Scheduling"
              value={currentStatus?.isInterviewEligible ? "Yes" : "No"}
            />
            <Row label="Allowed Transitions" value={transitionsLabel} />
          </div>
        </div>

        <div className="flex justify-end px-5 py-4 border-t border-border-primary bg-background-secondary">
          <Button
            onClick={onModelClose}
            className="rounded-full px-4 py-2 bg-button-secondary text-text-base"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewCandidateStatusModal;